import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiService } from '../../../../../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/services';
import {
	AiChatBotChatRequest,
	AiChatBotChatResponse,
	AiChatBotCreateProjectRequest,
	AiChatBotUpdateProjectRequest,
	AiChatBotFeedbackRequest,
	AiChatBotGetProjectRequest,
	AiChatBotGetProjectsRequest,
	AiChatBotDeleteProjectRequest,
	AiChatBotAssignConversationToProjectRequest,
	AiChatBotUpdateConversationPinRequest,
	AiChatBotUpdateConversationTitleRequest,
	AiChatBotDeleteConversationRequest,
} from '../../../../../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/models';

@Injectable({
	providedIn: 'root',
})
export class ChatApiService {
	constructor(private apiServiceV2: ApiService) {}

	async sendMessage({
		message,
		mode,
		conversationId,
		s3FileGUID,
		fileName,
		internalUserGUID,
	}: {
		mode: 'rag' | 'direct';
	} & AiChatBotChatRequest): Promise<AiChatBotChatResponse | undefined> {
		let response = undefined;

		switch (mode) {
			case 'rag':
				response = await lastValueFrom(this.apiServiceV2.sendRagMessage({ body: { message, conversationId, s3FileGUID, fileName, internalUserGUID } }));

				if (!response.isSuccess || !response.data) {
					return;
				}

				return response.data;
			case 'direct':
				response = await lastValueFrom(this.apiServiceV2.sendDirectMessage({ body: { message, conversationId, s3FileGUID, fileName, internalUserGUID } }));

				if (!response.isSuccess || !response.data) {
					return;
				}

				return response.data;
			default:
				throw new Error(`Unsupported chat mode: ${mode}`);
		}
	}

	async getConversations({ internalUserGUID }: { internalUserGUID: string }): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.getAiChatConversations({ body: { internalUserGUID } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async getConversation({ conversationId }: { conversationId: string }): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.getAiChatConversation({ body: { conversationId } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async submitFeedback({ feedback }: { feedback: AiChatBotFeedbackRequest }): Promise<any | undefined> {
		const { comment, internalUserGUID, messageId, rating } = feedback;

		const response = await lastValueFrom(this.apiServiceV2.submitAiChatFeedback({ body: { comment, internalUserGUID, messageId, rating } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async getProject({ projectId }: AiChatBotGetProjectRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.getAiChatBotProject({ body: { projectId } }));

		if (!response.isSuccess || !response.data) {
			return;
		}
		return response.data;
	}

	async getProjects({ internalUserGUID }: AiChatBotGetProjectsRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.getAiChatBotProjects({ body: { internalUserGUID } }));
		if (!response.isSuccess || !response.data) {
			return;
		}
		return response.data;
	}

	async createProject({ name, color, internalUserGUID }: AiChatBotCreateProjectRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.createAiChatBotProject({ body: { name, color, internalUserGUID } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async updateProject({ projectId, name, color }: AiChatBotUpdateProjectRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.updateAiChatBotProject({ body: { projectId, name, color } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async deleteProject({ projectId }: AiChatBotDeleteProjectRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.deleteAiChatBotProject({ body: { projectId } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async assignConversationToProject({ conversationId, projectId }: AiChatBotAssignConversationToProjectRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.assignConversationToProject({ body: { conversationId, projectId } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async renameConversation({ conversationId, title }: AiChatBotUpdateConversationTitleRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.renameAiChatBotConversation({ body: { conversationId, title } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async pinConversation({ conversationId, pinned }: AiChatBotUpdateConversationPinRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.updateAiChatConversationPin({ body: { conversationId, pinned } }));

		if (!response.isSuccess || !response.data) {
			return;
		}

		return response.data;
	}

	async deleteConversation({ conversationId }: AiChatBotDeleteConversationRequest): Promise<any | undefined> {
		const response = await lastValueFrom(this.apiServiceV2.deleteAiChatConversation({ body: { conversationId } }));

		if (!response.isSuccess || !response.data) {
			return;
		}
		return response.data;
	}
}
